import { createStore } from "zustand/vanilla";
import type { KeyValueStorage } from "../adapters";
import { EMPTY_DRAFT, isEmptyDraft, type OrderDraft, parseDraft } from "../ordering/draft";

/**
 * Drafts of orders, by `<restaurantId>:<draftKey>`, shared by every screen
 * (the order and the item picker beside it) and kept in the platform's
 * storage so they survive a reload or an app restart (UX rule 4).
 */
export type OrderDraftsState = {
	drafts: Readonly<Record<string, OrderDraft>>;
	/** Keys read from storage (or started) already. */
	loaded: Readonly<Record<string, true>>;
	load(key: string): Promise<void>;
	change(key: string, change: (draft: OrderDraft) => OrderDraft): void;
};

export type OrderDraftsStore = ReturnType<typeof createOrderDraftsStore>;

const PREFIX = "luckymenu.orderDraft.";

export function createOrderDraftsStore(storage: KeyValueStorage) {
	const reading = new Map<string, Promise<void>>();
	return createStore<OrderDraftsState>()((set, get) => ({
		drafts: {},
		loaded: {},
		load(key) {
			if (get().loaded[key]) return Promise.resolve();
			const known = reading.get(key);
			if (known) return known;
			const done = (draft: OrderDraft | null) => {
				reading.delete(key);
				// A change made while reading wins over what was stored.
				if (get().loaded[key]) return;
				set((s) => ({
					drafts: draft ? { ...s.drafts, [key]: draft } : s.drafts,
					loaded: { ...s.loaded, [key]: true },
				}));
			};
			const promise = storage.getItem(PREFIX + key).then(
				(raw) => done(parseDraft(raw)),
				() => done(null),
			);
			reading.set(key, promise);
			return promise;
		},
		change(key, change) {
			const next = change(get().drafts[key] ?? EMPTY_DRAFT);
			set((s) => ({ drafts: { ...s.drafts, [key]: next }, loaded: { ...s.loaded, [key]: true } }));
			// Every change is written at once, so nothing picked is lost to a crash.
			const write = isEmptyDraft(next)
				? storage.removeItem(PREFIX + key)
				: storage.setItem(PREFIX + key, JSON.stringify(next));
			void write.catch(() => {});
		},
	}));
}
